(function ($, window, document) {
    'use strict';

    /**
     * Represents the configuration and structure for the AI Chat component,
     * encapsulating different aspects like state management, configuration options,
     * visual icons, and DOM element selectors.
     *
     * @namespace AIChat
     */
    const AIChat = {
        /**
         * Represents the state of a component or application.
         * This object contains various properties that track the current state of behaviors,
         * UI interactions, and operational statuses.
         *
         * @property {boolean} isOpen Indicates whether the main component or feature is open.
         * @property {boolean} hasTyped Tracks if the user has typed any input.
         * @property {string} lang Represents the current language setting.
         * @property {?string} sessionId The unique identifier for the current session or user session context.
         * @property {boolean} submitTriggeredByButton Indicates if the submission process was triggered by a button click.
         * @property {boolean} isCheckingHealth Tracks if the health check process is ongoing.
         * @property {boolean} isServiceStatusOpen Indicates whether the service status view is open.
         * @property {boolean} isSending Tracks whether a message or data is being sent.
         * @property {?Object} pendingAction Represents the action that is pending, including the type ('open' or 'send') and an optional message.
         * @property {?string} pendingUserMessageId The identifier for a user message that is currently pending processing.
         * @property {?string} pendingAssistantMessageId The identifier for an assistant message that is currently pending processing.
         * @property {boolean} hasHydratedHistory Indicates if the history has been loaded or hydrated into the current state.
         * @property {?Object} leadData Contains lead-related data, if applicable.
         * @property {boolean} isLeadMode Indicates if the application or component is in "lead mode."
         * @property {string} leadSuccessMessage A message displayed upon successfully capturing lead data.
         * @property {number} bodyScrollY Tracks the `y` position of the body scroll.
         */
        state: {
            isOpen: false,
            hasTyped: false,
            lang: 'en',
            sessionId: null,
            submitTriggeredByButton: false,
            isCheckingHealth: false,
            isServiceStatusOpen: false,
            isSending: false,
            pendingAction: null, // { type: 'open' | 'send', message?: string }
            pendingUserMessageId: null,
            pendingAssistantMessageId: null,
            hasHydratedHistory: false,
            leadData: null,
            isLeadMode: false,
            leadSuccessMessage: '',
            bodyScrollY: 0
        },

        /**
         * Configuration object containing settings used for retry attempts,
         * storage keys, and other operational parameters.
         *
         * Properties:
         * - `maxRequestRetries` (number): The maximum number of retry attempts allowed for failed requests.
         * - `retryDelayMs` (number): The delay in milliseconds between retry attempts.
         * - `storageSessionKey` (string): The key used to store the session ID in storage.
         * - `storageMessagesKey` (string): The key used to store chat messages in storage.
         * - `storageLeadStateKey` (string): The key used to store the lead state in storage.
         * - `storageLeadFormKey` (string): The key used to store the lead form data in storage.
         */
        config: {
            maxRequestRetries: 3,
            retryDelayMs: 500,
            storageSessionKey: 'dk_ai_chat_session_id',
            storageMessagesKey: 'dk_ai_chat_messages',
            storageLeadStateKey: 'dk_ai_chat_lead_state',
            storageLeadFormKey: 'dk_ai_chat_lead_form',
        },

        /**
         * Contains icon templates used within the application.
         * Each icon is represented by an HTML string.
         *
         * @property {string} loading - Represents the loading icon consisting of animated dots.
         */
        icons: {
            loading: '<span class="ai-chat__dots" aria-hidden="true"><span></span><span></span><span></span></span>',
        },

        /**
         * Object containing a collection of CSS selectors used to target specific DOM elements
         * within the AI chat component. These selectors are used for querying and interacting
         * with various elements of the AI chat interface.
         *
         * Properties:
         *  - root: Selector for the root element of the AI chat.
         *  - window: Selector for the AI chat window.
         *  - launcher: Selector for the chat launcher button.
         *  - externalTrigger: Selector for an external trigger to open the chat.
         *  - close: Selector for the button to close the chat.
         *  - langSwitch: Selector for the language switch control within the chat.
         *  - quick: Selector for the container housing quick response elements.
         *  - quickList: Selector for the list of quick response options.
         *  - quickBtn: Selector for individual quick response option buttons.
         *  - consent: Selector for the consent prompt container.
         *  - consentText: Selector for the text within the consent prompt.
         *  - title: Selector for the main chat title element.
         *  - subtitle: Selector for the chat subtitle element.
         *  - introTitle: Selector for the chat introductory section title.
         *  - introText: Selector for the chat introductory text.
         *  - warning: Selector for warning messages displayed in the chat.
         *  - input: Selector for the chat input field.
         *  - form: Selector for the form wrapping the input and submit button.
         *  - submit: Selector for the submit button within the form.
         *  - end: Selector for the end chat message or notification.
         *  - confirm: Selector for the confirmation dialog container.
         *  - confirmClose: Selector for the confirmation dialog close button.
         *  - confirmNo: Selector for the "No" button within the confirmation dialog.
         *  - confirmYes: Selector for the "Yes" button within the confirmation dialog.
         *  - confirmTitle: Selector for the title of the confirmation dialog.
         *  - confirmText: Selector for the text within the confirmation dialog.
         *  - messages: Selector for the container holding chat messages.
         *  - langSwitchLabel: Selector for the label associated with the language switch.
         *  - inputLabel: Selector for the label associated with the chat input field.
         *  - status: Selector for the container displaying chat status messages.
         *  - statusTitle: Selector for the status message title.
         *  - statusText: Selector for the status message body.
         *  - statusRetry: Selector for the retry button within the status message.
         *  - statusClose: Selector for the close button within the status message.
         *  - leadForm: Selector for the lead generation form within the chat.
         *  - leadSubmit: Selector for the submit button within the lead generation form.
         */
        selectors: {
            root: '#ai-chat',
            window: '#ai-chat-window',
            launcher: '.js-ai-chat-launcher',
            externalTrigger: '.js-ai-chat-external-trigger',
            close: '.js-ai-chat-close',
            langSwitch: '.js-ai-chat-lang-switch',
            quick: '.ai-chat__quick',
            quickList: '.js-ai-chat-quick-list',
            quickBtn: '.ai-chat__quick-btn',
            consent: '.js-ai-chat-consent',
            consentText: '.js-ai-chat-consent-text',
            title: '.js-ai-chat-title',
            subtitle: '.js-ai-chat-subtitle',
            introTitle: '.js-ai-chat-intro-title',
            introText: '.js-ai-chat-intro-text',
            warning: '.js-ai-chat-warning',
            input: '.js-ai-chat-input',
            form: '.js-ai-chat-form',
            submit: '.js-ai-chat-submit',
            end: '.js-ai-chat-end',
            confirm: '.js-ai-chat-confirm',
            confirmClose: '.js-ai-chat-confirm-close',
            confirmNo: '.js-ai-chat-confirm-no',
            confirmYes: '.js-ai-chat-confirm-yes',
            confirmTitle: '.js-ai-chat-confirm-title',
            confirmText: '.js-ai-chat-confirm-text',
            messages: '.js-ai-chat-messages',

            langSwitchLabel: 'label[for="ai-chat-lang"]',
            inputLabel: 'label[for="ai-chat-input"]',

            status: '.js-ai-chat-status',
            statusTitle: '.js-ai-chat-status-title',
            statusText: '.js-ai-chat-status-text',
            statusRetry: '.js-ai-chat-status-retry',
            statusClose: '.js-ai-chat-status-close',

            leadForm: '.js-ai-chat-lead-form',
            leadSubmit: '.js-ai-chat-lead-submit'
        },

        /**
         * Initializes the AI chat module by setting up the required state, synchronizing language settings,
         * and binding events. Performs the following key tasks:
         * - Detects the current language settings.
         * - Restores the session ID and lead state.
         * - Synchronizes the language switcher with the current state.
         * - Updates text content based on the detected language.
         * - Renders the quick questions interface.
         * - Binds necessary event listeners for user interaction.
         * - Fixes scrolling behavior for the textarea and messages container.
         *
         * @return {void} Does not return any value. The method sets up the module in preparation for user interaction.
         */
        init() {
            if (!$(this.selectors.root).length || !window.DK_AI || !window.DK_AI.i18n) {
                return;
            }

            this.detectLang();
            this.restoreSessionId();
            this.restoreLeadState();
            this.syncLangSwitch();
            this.updateTexts();
            this.renderQuickQuestions();
            this.bindEvents();
            this.bindTextareaScrollFix();
            this.bindMessagesScrollFix();

            $(this.selectors.root).removeClass('ai-chat--preload');
        },

        /**
         * Determines if the device being used is a touch-enabled device.
         *
         * @return {boolean} Returns true if the device supports touch input, otherwise false.
         */
        isTouchDevice() {
            return window.matchMedia('(pointer: coarse)').matches;
        },

        /**
         * Determines if the current viewport width qualifies as a mobile viewport.
         * A viewport is considered mobile if its width is 767 pixels or less.
         *
         * @return {boolean} True if the viewport width is 767 pixels or less, otherwise false.
         */
        isMobileViewport() {
            return window.matchMedia('(max-width: 767px)').matches;
        },

        /**
         * Determines if the current device is a mobile device by checking for touch capabilities
         * and a mobile viewport size.
         *
         * @return {boolean} Returns true if the device is a mobile device, otherwise false.
         */
        isMobileDevice() {
            return this.isTouchDevice() && this.isMobileViewport();
        },

        /**
         * Locks the body scroll on mobile devices, preventing vertical scrolling.
         * This is typically used in scenarios where overlay components or modals require disabling background scrolling.
         *
         * The method checks if the current device is a mobile device using the `isMobileDevice` method.
         * If true, it captures the current vertical scroll position and applies necessary styles
         * to freeze the body in place and prevent further scrolling.
         *
         * @return {void} Does not return a value.
         */
        lockMobileBodyScroll() {
            if (!this.isMobileDevice()) return;
            this.state.bodyScrollY = window.scrollY || window.pageYOffset || 0;
            $('html').addClass('ai-chat-scroll-locked');
            $('body')
                .addClass('ai-chat-scroll-locked')
                .css({
                    position: 'fixed',
                    top: '-' + this.state.bodyScrollY + 'px',
                    left: '0',
                    right: '0',
                    width: '100%',
                    overflow: 'hidden',
                });
        },

        /**
         * Unlocks the body scroll on mobile devices by removing specific CSS styles and resetting the scroll position.
         *
         * This method ensures that the body is no longer scroll-locked, allowing normal scrolling behavior on mobile devices.
         * It restores the original `scrollY` position stored in the component's state and removes scroll-lock related styles
         * from the HTML and body elements.
         *
         * @return {void} This method does not return a value.
         */
        unlockMobileBodyScroll() {
            if (!this.isMobileDevice()) return;
            const scrollY = this.state.bodyScrollY || 0;
            $('html').removeClass('ai-chat-scroll-locked');
            $('body')
                .removeClass('ai-chat-scroll-locked')
                .css({
                    position: '',
                    top: '',
                    left: '',
                    right: '',
                    width: '',
                    overflow: '',
                });
            window.scrollTo(0, scrollY);
        },

        /**
         * Detects the user's language preference from the browser's navigator object
         * or the HTML document's `lang` attribute. If neither provides a supported
         * language, defaults to English ('en').
         *
         * @return {void} Sets the detected language to the `state.lang` property.
         */
        detectLang() {
            const browserLang = String(
                window.navigator.language || window.navigator.userLanguage || ''
            ).toLowerCase().split('-')[0];

            if (browserLang && DK_AI.i18n[browserLang]) {
                this.state.lang = browserLang;
                return;
            }

            const htmlLang = String(document.documentElement.lang || '')
                .toLowerCase()
                .split('-')[0];

            if (htmlLang && DK_AI.i18n[htmlLang]) {
                this.state.lang = htmlLang;
                return;
            }

            this.state.lang = 'en';
        },

        /**
         * Synchronizes the language switcher UI with the current application state.
         *
         * Updates the value of the language switcher element to reflect the current language stored in the application state.
         *
         * @return {void} Does not return a value.
         */
        syncLangSwitch() {
            $(this.selectors.langSwitch).val(this.state.lang);
        },

        /**
         * Retrieves the locale object based on the current language state.
         * If the specified language is not available, it falls back to English or an empty object.
         *
         * @return {Object} The locale object corresponding to the current language or a fallback.
         */
        getLocale() {
            return DK_AI.i18n[this.state.lang] || DK_AI.i18n.en || {};
        },

        /**
         * Retrieves the labels from the current locale.
         *
         * @return {Object} An object containing the labels for the current locale.
         * If no labels are defined, an empty object is returned.
         */
        getLabels() {
            return this.getLocale().labels || {};
        },

        /**
         * Retrieves localized service-related text based on the current locale.
         * If service texts for the current locale are not available, it falls back to default English service texts.
         *
         * @return {Object} An object containing service-related text for the current locale or default English service text if none are found.
         */
        getServiceTexts() {
            const locale = this.getLocale();
            const service = locale.service || {};

            if (Object.keys(service).length) {
                return service;
            }

            if (DK_AI.i18n.en && DK_AI.i18n.en.service) {
                return DK_AI.i18n.en.service;
            }

            return {};
        },

        /**
         * Retrieves the contact text information based on the current locale.
         * If no contact data is available for the current locale, it falls back to the default English contact data.
         *
         * @return {Object} An object containing the contact text information for the determined locale, or an empty object if no data is available.
         */
        getContactTexts() {
            const locale = this.getLocale();
            const contact = locale.contact || {};

            if (Object.keys(contact).length) {
                return contact;
            }

            if (DK_AI.i18n.en && DK_AI.i18n.en.contact) {
                return DK_AI.i18n.en.contact;
            }

            return {};
        },

        /**
         * Translates a given key based on the current locale. If the key is not found in the current locale,
         * it falls back to the default English locale. If the key is not available in either, the key itself is returned.
         *
         * @param {string} key - The key used to retrieve a corresponding translation from the locale.
         * @return {string} The translated value if found in the locale or fallback, otherwise the input key itself.
         */
        t(key) {
            const locale = this.getLocale();

            if (Object.prototype.hasOwnProperty.call(locale, key)) {
                return locale[key];
            }

            if (DK_AI.i18n.en && Object.prototype.hasOwnProperty.call(DK_AI.i18n.en, key)) {
                return DK_AI.i18n.en[key];
            }

            return key;
        },

        /**
         * Translates a given key into its corresponding label.
         * If the key exists in the local labels, it will return the corresponding value.
         * If not, it will look for the key in the predefined English labels and return its value if found.
         * If the key is not found in either, it will return the original key.
         *
         * @param {string} key - The key to be translated into its corresponding label.
         * @return {string} The translated label if the key exists, otherwise the original key.
         */
        tl(key) {
            const labels = this.getLabels();

            if (Object.prototype.hasOwnProperty.call(labels, key)) {
                return labels[key];
            }

            if (
                DK_AI.i18n.en &&
                DK_AI.i18n.en.labels &&
                Object.prototype.hasOwnProperty.call(DK_AI.i18n.en.labels, key)
            ) {
                return DK_AI.i18n.en.labels[key];
            }

            return key;
        },

        /**
         * Retrieves the text value associated with the given key from the service texts or defaults to a fallback value.
         *
         * @param {string} key - The key to look up in the service texts.
         * @return {string} The text associated with the key if found, otherwise the key itself.
         */
        ts(key) {
            const service = this.getServiceTexts();

            if (Object.prototype.hasOwnProperty.call(service, key)) {
                return service[key];
            }

            if (
                DK_AI.i18n.en &&
                DK_AI.i18n.en.service &&
                Object.prototype.hasOwnProperty.call(DK_AI.i18n.en.service, key)
            ) {
                return DK_AI.i18n.en.service[key];
            }

            return key;
        },

        /**
         * Retrieves a specific text value associated with the provided key from contact texts.
         * If the key is not found in the contact texts, attempts to retrieve it from a default English contact set.
         * If the key is not found in either, returns the key itself.
         *
         * @param {string} key - The key to look up in the contact texts.
         * @return {string} The text value associated with the key, or the key itself if no matching value is found.
         */
        tc(key) {
            const contact = this.getContactTexts();

            if (Object.prototype.hasOwnProperty.call(contact, key)) {
                return contact[key];
            }

            if (
                DK_AI.i18n.en &&
                DK_AI.i18n.en.contact &&
                Object.prototype.hasOwnProperty.call(DK_AI.i18n.en.contact, key)
            ) {
                return DK_AI.i18n.en.contact[key];
            }

            return key;
        },

        /**
         * Retrieves a list of questions based on the current locale. If the current locale does not have questions,
         * it falls back to the default English locale. If neither contain questions, an empty array is returned.
         *
         * @return {Array} An array of questions corresponding to the locale or an empty array if no questions are available.
         */
        getQuestions() {
            const locale = this.getLocale();

            if (Array.isArray(locale.questions) && locale.questions.length) {
                return locale.questions;
            }

            if (DK_AI.i18n.en && Array.isArray(DK_AI.i18n.en.questions)) {
                return DK_AI.i18n.en.questions;
            }

            return [];
        },

        /**
         * Binds various event listeners to DOM elements to manage interactions such as clicks, inputs, and form submissions
         * within the application. These events handle tasks like opening/closing UI components, updating states, and
         * submitting data.
         *
         * @return {void} Does not return a value.
         */
        bindEvents() {
            $(document).on('click', this.selectors.launcher, async (e) => {
                e.preventDefault();

                if (this.state.isOpen) {
                    this.close();
                    return;
                }

                await this.tryOpenWithHealthCheck();
            });

            $(document).on('click', this.selectors.externalTrigger, async (e) => {
                e.preventDefault();

                if (this.state.isOpen) {
                    return;
                }

                await this.tryOpenWithHealthCheck();
            });

            $(document).on('click', this.selectors.statusRetry, async (e) => {
                e.preventDefault();
                await this.retryPendingAction();
            });

            $(document).on('click', this.selectors.statusClose, (e) => {
                e.preventDefault();
                this.closeServiceStatus();
            });

            $(document).on('click', this.selectors.close, (e) => {
                e.preventDefault();
                this.close();
            });

            $(document).on('change', this.selectors.langSwitch, (e) => {
                const value = String($(e.currentTarget).val() || '').trim().toLowerCase();

                if (!DK_AI.i18n[value]) {
                    return;
                }

                this.state.lang = value;
                this.updateTexts();
            });

            $(document).on('click', this.selectors.quickBtn, async (e) => {
                e.preventDefault();
                const text = $(e.currentTarget).text().trim();
                await this.fillInput(text);
            });

            $(document).on('input', this.selectors.input, (e) => {
                const value = String(e.currentTarget.value || '').trim();

                if (!this.state.hasTyped && value.length > 0) {
                    this.state.hasTyped = true;
                    $(this.selectors.consent).stop(true, true).slideUp(150);
                }
            });

            $(document).on('click', this.selectors.submit, () => {
                this.state.submitTriggeredByButton = true;
            });

            $(document).on('submit', this.selectors.form, async (e) => {
                e.preventDefault();

                if (!this.state.submitTriggeredByButton) {
                    return;
                }

                this.state.submitTriggeredByButton = false;

                if (this.state.isSending) {
                    return;
                }

                const value = String($(this.selectors.input).val() || '').trim();
                if (!value.length) {
                    $(this.selectors.input).trigger('focus');
                    return;
                }

                await this.submitChatMessage(value);
            });

            $(document).on('submit', this.selectors.leadForm, (e) => {
                e.preventDefault();
                this.handleLeadFormSubmit($(e.currentTarget));
            });

            $(document).on('input', '.js-ai-chat-lead-form input', (e) => {
                const $form = $(e.currentTarget).closest(this.selectors.leadForm);
                this.persistLeadFormData(this.getLeadFormDataFromDom($form));
            });

            $(document).on('click', this.selectors.end, (e) => {
                e.preventDefault();
                this.openConfirm();
            });

            $(document).on('click', this.selectors.confirmClose, (e) => {
                e.preventDefault();
                this.closeConfirm();
            });

            $(document).on('click', this.selectors.confirmNo, (e) => {
                e.preventDefault();
                this.closeConfirm();
            });

            $(document).on('click', this.selectors.confirmYes, (e) => {
                e.preventDefault();
                this.endChat();
            });

            $(document).on('keydown', (e) => {
                if (e.key !== 'Escape') {
                    return;
                }

                if (this.state.isServiceStatusOpen) {
                    this.closeServiceStatus();
                    return;
                }

                if (this.isConfirmOpen()) {
                    this.closeConfirm();
                    return;
                }

                if (this.state.isOpen) {
                    this.close();
                }
            });
        },

        /**
         * Opens the UI component by updating its state and attributes, rendering necessary elements,
         * and focusing on the input field after a brief delay. Handles lead mode specific actions
         * such as rendering and populating the lead form.
         *
         * @return {void} This method does not return any value.
         */
        open() {
            const $root = $(this.selectors.root);
            const $window = $(this.selectors.window);
            const $launcher = $(this.selectors.launcher);

            this.closeServiceStatus();
            this.hydrateStoredMessages();

            if (this.state.isLeadMode && this.state.leadData) {
                this.renderLeadForm();
                this.setLeadModeUi(true);
                this.fillLeadFormFromStorage();
            } else if (this.state.isLeadMode && !this.state.leadData) {
                this.clearLeadState();
                this.clearLeadFormData();
            }

            $root.attr('data-state', 'open');
            $window.attr('aria-hidden', 'false');
            $launcher.attr('aria-expanded', 'true');

            this.state.isOpen = true;
            this.lockMobileBodyScroll();

            if (!this.isTouchDevice()) {
                window.setTimeout(() => {
                    $(this.selectors.input).trigger('focus');
                }, 80);
            }
        },

        /**
         * Closes the specified UI component, updates attributes and states, and restores focus to the launcher element.
         * Performs necessary cleanup actions such as resetting the state and ensuring accessibility attributes are updated.
         *
         * @return {void} This method does not return a value.
         */
        close() {
            const $root = $(this.selectors.root);
            const $window = $(this.selectors.window);
            const $launcher = $(this.selectors.launcher);

            this.closeConfirm({restoreFocus: false});
            this.closeServiceStatus();

            if (document.activeElement && typeof document.activeElement.blur === 'function') {
                document.activeElement.blur();
            }

            $root.attr('data-state', 'closed');
            $launcher.attr('aria-expanded', 'false');

            window.requestAnimationFrame(() => {
                const launcherEl = $launcher.get(0);

                if (launcherEl && typeof launcherEl.focus === 'function') {
                    launcherEl.focus({preventScroll: true});
                }

                window.requestAnimationFrame(() => {
                    $window.attr('aria-hidden', 'true');
                });
            });

            this.state.isOpen = false;
            this.unlockMobileBodyScroll();
        },

        /**
         * Opens the confirmation dialog by making the confirm element visible,
         * provided the service status is not already open.
         *
         * @return {void} This method does not return a value.
         */
        openConfirm() {
            if (this.state.isServiceStatusOpen) {
                return;
            }

            $(this.selectors.confirm).prop('hidden', false);
        },

        /**
         * Closes the confirmation dialog and optionally restores focus to an input element.
         *
         * @param {Object} [options={}] Options to customize the behavior of the method.
         * @param {boolean} [options.restoreFocus=true] Determines whether to restore focus to the input element after closing the dialog.
         * @return {void} This method does not return a value.
         */
        closeConfirm(options = {}) {
            const settings = {
                restoreFocus: true,
                ...options,
            };

            $(this.selectors.confirm).prop('hidden', true);

            if (settings.restoreFocus && this.state.isOpen) {
                $(this.selectors.input).trigger('focus');
            }
        },

        /**
         * Checks whether the confirm dialog is open.
         *
         * Evaluates the 'hidden' property of the confirm dialog element
         * to determine if it is currently visible.
         *
         * @return {boolean} True if the confirm dialog is open, otherwise false.
         */
        isConfirmOpen() {
            return $(this.selectors.confirm).prop('hidden') === false;
        },

        /**
         * Hides the quick questions element by stopping any ongoing animations
         * and sliding it up with an animation duration of 150 milliseconds.
         *
         * @return {void} This method does not return a value.
         */
        hideQuickQuestions() {
            $(this.selectors.quick).stop(true, true).slideUp(150);
        },

        /**
         * Displays the quick questions section by sliding it down with a specified animation.
         *
         * @return {void} This method does not return any value.
         */
        showQuickQuestions() {
            $(this.selectors.quick).stop(true, true).slideDown(150);
        },

        /**
         * Ends the current chat session by clearing all relevant states, session data, and UI components,
         * resetting chat input and interface elements, and restoring the initial chat state.
         *
         * @return {void} This method does not return a value.
         */
        endChat() {
            this.clearSessionId();
            this.clearStoredMessages();
            this.clearLeadState();
            this.clearLeadFormData();

            this.state.hasTyped = false;
            this.state.pendingAction = null;
            this.state.pendingUserMessageId = null;
            this.state.pendingAssistantMessageId = null;
            this.state.isSending = false;
            this.state.hasHydratedHistory = false;
            this.state.leadSuccessMessage = '';

            if (document.activeElement && typeof document.activeElement.blur === 'function') {
                document.activeElement.blur();
            }

            this.closeConfirm({restoreFocus: false});
            this.closeServiceStatus();
            this.setComposerBusy(false);

            $(this.selectors.input).val('');
            $(this.selectors.consent).stop(true, true).show();
            this.setLeadModeUi(false);
            $(this.selectors.messages).html(this.getIntroMessageHtml());

            this.showQuickQuestions();
            this.updateTexts();
            this.close();
        },

        /**
         * Fills the input field with the provided text and handles related state changes.
         *
         * @param {string} text - The text to fill into the input field.
         * @return {Promise<void>} A promise that resolves when the input action is complete.
         */
        async fillInput(text) {
            const $input = $(this.selectors.input);

            if (!this.state.isOpen) {
                await this.tryOpenWithHealthCheck();

                if (!this.state.isOpen) {
                    return;
                }
            }

            $input.val(text);

            if (!this.state.hasTyped && String(text).trim().length > 0) {
                this.state.hasTyped = true;
                $(this.selectors.consent).stop(true, true).slideUp(150);
            }

            this.hideQuickQuestions();
            $input.trigger('focus');
        },

        /**
         * Renders quick question buttons based on a list of predefined questions and updates the DOM accordingly.
         *
         * The method generates HTML for each question by mapping over the list of questions, escaping the content for safety,
         * and wrapping it in a button element. The compiled HTML is then inserted into the designated quick question container.
         * Additionally, an aria-label is set for accessibility.
         *
         * @return {void} This method does not return a value.
         */
        renderQuickQuestions() {
            const html = this.getQuestions().map((question) => {
                return '<button type="button" class="ai-chat__quick-btn">' + this.escapeHtml(question) + '</button>';
            }).join('');

            $(this.selectors.quickList).html(html);
            $(this.selectors.quick).attr('aria-label', this.tl('suggested_questions'));
        },

        /**
         * Updates the text content and attributes of various UI elements using translations.
         * This method dynamically updates the text, aria-labels, and placeholders for the elements
         * specified in the `selectors` properties, ensuring they align with the current language
         * and context. It also refreshes the buttons, handles lead mode UI updates, and invokes
         * quick questions rendering.
         *
         * @return {void} Does not return a value.
         */
        updateTexts() {
            const $window = $(this.selectors.window);
            const $langSwitch = $(this.selectors.langSwitch);
            const $close = $(this.selectors.close);
            const $input = $(this.selectors.input);

            $(this.selectors.title).text(this.t('title'));
            $(this.selectors.subtitle).text(this.t('subtitle'));
            $(this.selectors.introTitle).text(this.t('assistant_name'));
            $(this.selectors.introText).text(this.t('assistant_start_message'));
            $(this.selectors.warning).html(this.t('warning'));
            $(this.selectors.consentText).html(this.t('consent'));

            $window.attr('aria-label', this.t('title'));
            $close.attr('aria-label', this.tl('close_chat'));
            $(this.selectors.statusClose).attr('aria-label', this.tl('close_chat'));

            $(this.selectors.langSwitchLabel).text(this.tl('choose_language'));
            $(this.selectors.inputLabel).text(this.tl('your_message'));

            $langSwitch.attr('aria-label', this.tl('choose_language'));
            $input
                .attr('placeholder', this.t('placeholder'))
                .attr('aria-label', this.tl('your_message'));

            $(this.selectors.confirmTitle).text(this.tl('end_chat_confirm_title'));
            $(this.selectors.confirmText).text(this.tl('end_chat_confirm_text'));

            $(this.selectors.statusTitle).text(this.ts('unavailable_title'));
            $(this.selectors.statusText).text(this.ts('unavailable_text'));

            this.renderQuickQuestions();

            if (this.state.isLeadMode) {
                this.setLeadModeUi(true);
                this.refreshLeadFormTexts();
            }

            this.refreshButtons();
        },

        /**
         * Builds and animates the caption for a button by transforming the text into
         * styled blocks and letter elements.
         *
         * @param {jQuery} $caption - A jQuery object representing the caption element.
         *                            This element will be modified to include animated blocks.
         * @param {string} text - The text to be displayed and animated in the caption. If
         *                        no text is provided, an empty string is used.
         * @return {void} - This method does not return a value.
         */
        buildAnimatedButtonCaption($caption, text) {
            if (!$caption.length) {
                return;
            }

            const safeText = String(text || '');
            $caption.empty();

            const createBlock = () => {
                const $block = $('<div/>', {class: 'btn-anim__block'});

                for (const letter of safeText) {
                    $('<span/>', {
                        class: 'btn-anim__letter',
                        text: letter.trim() === '' ? '\u00A0' : letter,
                    }).appendTo($block);
                }

                return $block;
            };

            $caption.append(createBlock());
            $caption.append(createBlock());
            $caption.removeClass('play');
        },

        /**
         * Updates the text and animations for various buttons in the UI.
         *
         * This method dynamically updates the captions of multiple buttons based on the current application state
         * and localized text. It ensures the text is properly set and animations, if applicable, are applied to the
         * button captions.
         *
         * @return {void} Does not return a value.
         */
        refreshButtons() {
            this.buildAnimatedButtonCaption(
                $(this.selectors.submit).find('.btn-caption'),
                this.tl('send')
            );

            $(this.selectors.end).text(this.tl('end_chat'));

            this.buildAnimatedButtonCaption(
                $(this.selectors.confirmNo).find('.btn-caption'),
                this.tl('end_chat_confirm_no')
            );

            this.buildAnimatedButtonCaption(
                $(this.selectors.confirmYes).find('.btn-caption'),
                this.tl('end_chat_confirm_yes')
            );

            this.buildAnimatedButtonCaption(
                $(this.selectors.statusRetry).find('.btn-caption'),
                this.state.isCheckingHealth ? this.ts('checking') : this.ts('unavailable_retry')
            );
        },

        /**
         * Attaches event listeners to a textarea element to fix scrolling issues.
         *
         * This method binds the `wheel` and `touchmove` events to a textarea
         * identified by the selector defined in `this.selectors.input`. The
         * `wheel` event prevents outer scrolling when the textarea can still scroll
         * internally, while the `touchmove` event stops touch propagation for better
         * control of scrolling behavior.
         *
         * @return {void} This method does not return a value.
         */
        bindTextareaScrollFix() {
            const input = document.querySelector(this.selectors.input);

            if (!input) {
                return;
            }

            input.addEventListener('wheel', this.preventOuterScrollWhenInnerCanScroll, {passive: false});
            input.addEventListener('touchmove', this.stopTouchPropagation, {passive: true});
        },

        /**
         * Binds event listeners to a messages container to handle scroll behavior and touch propagation.
         * Listens for `wheel` events to prevent outer container scrolling when the inner messages container can scroll.
         * Also listens for `touchmove` events to stop touch propagation outside the messages container.
         *
         * @return {void} This method does not return a value.
         */
        bindMessagesScrollFix() {
            const messages = document.querySelector(this.selectors.messages);

            if (!messages) {
                return;
            }

            messages.addEventListener('wheel', this.preventOuterScrollWhenInnerCanScroll, {passive: false});
            messages.addEventListener('touchmove', this.stopTouchPropagation, {passive: true});
        },

        /**
         * Prevents the outer scrollable container from scrolling when the inner scrollable container can handle the scroll event.
         *
         * @param {WheelEvent} event The wheel event triggered during scroll, containing details about the scroll direction and target element.
         * @return {void} No return value.
         */
        preventOuterScrollWhenInnerCanScroll(event) {
            const el = event.currentTarget;
            const deltaY = event.deltaY;
            const canScroll = el.scrollHeight > el.clientHeight;
            const isAtTop = el.scrollTop <= 0;
            const isAtBottom = Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight;

            if (!canScroll) {
                return;
            }

            const willScrollUpInside = deltaY < 0 && !isAtTop;
            const willScrollDownInside = deltaY > 0 && !isAtBottom;

            if (willScrollUpInside || willScrollDownInside) {
                event.preventDefault();
                event.stopPropagation();
                el.scrollTop += deltaY;
            }
        },

        /**
         * Stops further propagation of the provided touch event in the event chain.
         *
         * @param {Event} event - The event object that should have its propagation stopped.
         * @return {void} No return value.
         */
        stopTouchPropagation(event) {
            event.stopPropagation();
        },

        /**
         * Attempts to open the service after performing a health check.
         * If a health check is already in progress, the method will exit early.
         * The process includes marking the current action as "open," closing the
         * service status indicator, and setting a loading state for the health check.
         * It then proceeds to execute a health check with retries.
         *
         * If the health check is successful and the service status is "ok," the method
         * clears the pending action and opens the service. Otherwise, it opens the
         * service status indicator to display the failure state.
         *
         * Any errors encountered during the health check are logged, and the service
         * status indicator is opened to reflect the failure state. Finally, the health
         * check loading state is cleared after the process completes.
         *
         * @return {Promise<void>} A promise that resolves once the health check and any
         * associated actions (e.g., opening the service or handling errors) have been
         * completed.
         */
        async tryOpenWithHealthCheck() {
            if (this.state.isCheckingHealth) {
                return;
            }

            this.state.pendingAction = {type: 'open'};
            this.closeServiceStatus();
            this.setHealthCheckLoading(true);

            try {
                const health = await this.fetchHealthWithRetry();

                if (health && health.status === 'ok') {
                    this.state.pendingAction = null;
                    this.open();
                    return;
                }

                this.openServiceStatus();
            } catch (error) {
                console.log('[AI Chat]: /health request failed:', error);
                this.openServiceStatus();
            } finally {
                this.setHealthCheckLoading(false);
            }
        },

        /**
         * Retries the pending action stored in the component's state, if any.
         * If there is no pending action, attempts to open the connection with a health check.
         * For 'send' type pending actions, retries submitting a chat message marked as a retry.
         *
         * @return {Promise<void>} A promise that resolves when the pending action is retried or the connection is opened.
         */
        async retryPendingAction() {
            if (!this.state.pendingAction) {
                await this.tryOpenWithHealthCheck();
                return;
            }

            if (this.state.pendingAction.type === 'send' && this.state.pendingAction.message) {
                await this.submitChatMessage(this.state.pendingAction.message, {isRetry: true});
                return;
            }

            await this.tryOpenWithHealthCheck();
        },

        /**
         * Handles the submission of a chat message by the user.
         * It processes the message, performs necessary checks, sends the request to the server,
         * and manages the UI updates for the chat flow, including appending and updating messages.
         *
         * @param {string} message - The message content provided by the user.
         * @param {Object} [options={}] - Additional options for the message submission.
         * @param {boolean} [options.isRetry=false] - Identifies if this is a retry attempt for sending the message.
         * @param {boolean} [options.skipHealthCheck=false] - Skips the service health check if set to true.
         *
         * @return {Promise<void>} Resolves after processing the message submission, including API calls and UI updates.
         */
        async submitChatMessage(message, options = {}) {
            const settings = {
                isRetry: false,
                skipHealthCheck: false,
                ...options,
            };

            const cleanMessage = String(message || '').trim();
            if (!cleanMessage.length || this.state.isSending) {
                return;
            }

            if (!settings.skipHealthCheck) {
                this.state.pendingAction = {
                    type: 'send',
                    message: cleanMessage,
                };
                this.setComposerBusy(true);
                try {
                    const health = await this.fetchHealthWithRetry();
                    if (!health || health.status !== 'ok') {
                        this.setComposerBusy(false);
                        this.openServiceStatus();
                        return;
                    }
                } catch (error) {
                    console.log('[AI Chat]: /health request failed:', error);
                    this.setComposerBusy(false);
                    this.openServiceStatus();
                    return;
                }
            }

            if (!this.state.isOpen) {
                this.open();
            }

            this.closeServiceStatus();
            this.state.pendingAction = {
                type: 'send',
                message: cleanMessage,
            };

            const $input = $(this.selectors.input);
            const originalValue = cleanMessage;

            this.hideQuickQuestions();

            const pendingUserMessageId = this.appendMessage({
                role: 'user',
                text: cleanMessage,
                pending: true,
            });

            const pendingAssistantMessageId = this.appendMessage({
                role: 'assistant',
                speaker: this.t('assistant_name'),
                loading: true,
                pending: true,
            });

            this.state.pendingUserMessageId = pendingUserMessageId;
            this.state.pendingAssistantMessageId = pendingAssistantMessageId;
            this.state.isSending = true;
            this.setComposerBusy(true);

            $input.val('');

            if (!this.state.hasTyped && cleanMessage.length > 0) {
                this.state.hasTyped = true;
                $(this.selectors.consent).stop(true, true).slideUp(150);
            }

            const requestStart = window.performance.now();

            try {
                const response = await this.sendChatRequestWithRetry({
                    message: cleanMessage,
                    session_id: this.state.sessionId,
                });

                const requestTotalMs = Math.max(0, Math.round(window.performance.now() - requestStart));
                const isLeadHandoff = this.isLeadHandoffResponse(response);
                const renderAsError = this.shouldRenderResponseAsError(response);

                this.markMessageAsSent(pendingUserMessageId);
                this.state.pendingUserMessageId = null;
                this.state.pendingAction = null;
                this.persistSessionId(response && response.session_id ? response.session_id : this.state.sessionId);

                if (isLeadHandoff) {
                    this.removeMessageById(pendingAssistantMessageId);
                    this.state.pendingAssistantMessageId = null;

                    this.state.leadData = response && response.lead ? response.lead : null;
                    this.state.isLeadMode = true;
                    this.persistLeadState();

                    this.renderLeadForm();
                    this.setLeadModeUi(true);
                    this.fillLeadFormFromStorage();
                    this.appendLatencyMeta(response, requestTotalMs);

                    window.dispatchEvent(new window.CustomEvent('aiChat:leadHandoff', {
                        detail: response,
                    }));
                } else {
                    this.replaceAssistantLoadingMessage(pendingAssistantMessageId, {
                        role: renderAsError ? 'error' : 'assistant',
                        speaker: this.t('assistant_name'),
                        text: String(response && response.answer ? response.answer : ''),
                    });

                    this.state.pendingAssistantMessageId = null;
                    this.appendLatencyMeta(response, requestTotalMs);

                    if (this.shouldPersistConversationTurn(response, cleanMessage)) {
                        this.persistConversationTurn(cleanMessage, String(response.answer || ''));
                    }
                }

                $input.trigger('focus');
            } catch (error) {
                console.log('[AI Chat]: /chat request failed:', error);

                this.removeMessageById(pendingUserMessageId);
                this.removeMessageById(pendingAssistantMessageId);

                this.state.pendingUserMessageId = null;
                this.state.pendingAssistantMessageId = null;

                $input.val(originalValue).trigger('focus');

                this.appendMessage({
                    role: 'error',
                    text: this.getChatErrorMessage(error),
                });
            } finally {
                this.state.isSending = false;
                this.setComposerBusy(false);
            }
        },

        /**
         * Sends a chat request to the chat API endpoint with retry logic in case of specific HTTP status errors.
         *
         * @param {Object} payload The payload to be sent with the chat request, which must be properly structured and serializable to JSON format.
         * @return {Promise<Object>} A promise that resolves to the parsed JSON response from the API if the request succeeds, or rejects with an error if all retry attempts fail.
         */
        async sendChatRequestWithRetry(payload) {
            return await this.requestJsonWithRetry(
                this.getApiUrl('/api/chat'),
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                    credentials: 'omit',
                    body: JSON.stringify(payload),
                },
                {
                    retries: this.config.maxRequestRetries,
                    retryOnStatuses: [408, 429, 502, 503, 504],
                }
            );
        },

        /**
         * Attempts to fetch the health status of a given API endpoint with retry logic.
         * The method performs a GET request to the /health endpoint and retries
         * the request based on the specified retry configuration.
         *
         * @return {Promise<Object>} A promise that resolves to the JSON response
         * containing the health status of the API.
         */
        async fetchHealthWithRetry() {
            return await this.requestJsonWithRetry(
                this.getApiUrl('/health'),
                {
                    method: 'GET',
                    headers: {
                        Accept: 'application/json',
                    },
                    credentials: 'omit',
                },
                {
                    retries: this.config.maxRequestRetries,
                    retryOnStatuses: [408, 429, 502, 503, 504],
                }
            );
        },

        /**
         * Sends a JSON request to the given URL with retry logic.
         *
         * Retries the request based on the configured retry settings in case of network-like issues
         * or specific HTTP status codes.
         *
         * @param {string} url - The URL to which the request is sent.
         * @param {object} options - The options for the `fetch` API, such as method, headers, and body.
         * @param {object} [settings] - Optional settings for retry behavior.
         * @param {number} [settings.retries=1] - The number of retry attempts to make before failing.
         * @param {number[]} [settings.retryOnStatuses=[]] - Array of HTTP status codes for which the request should be retried.
         * @return {Promise<object>} Resolves to the response payload if the request succeeds.
         *                           Rejects with an error if the request fails after all retry attempts.
         */
        async requestJsonWithRetry(url, options, settings = {}) {
            const retries = Number(settings.retries || 1);
            const retryOnStatuses = Array.isArray(settings.retryOnStatuses) ? settings.retryOnStatuses : [];
            let lastError = null;

            for (let attempt = 1; attempt <= retries; attempt += 1) {
                try {
                    const response = await window.fetch(url, options);
                    const data = await this.readResponsePayload(response);

                    if (!response.ok) {
                        const error = new Error('Request failed with status ' + response.status);
                        error.response = response;
                        error.status = response.status;
                        error.payload = data;

                        if (attempt < retries && retryOnStatuses.indexOf(response.status) !== -1) {
                            await this.wait(this.getRetryDelay(attempt));
                            continue;
                        }

                        throw error;
                    }

                    return data;
                } catch (error) {
                    lastError = error;

                    const isNetworkLike = !error.response;
                    if (attempt < retries && isNetworkLike) {
                        await this.wait(this.getRetryDelay(attempt));
                        continue;
                    }

                    throw error;
                }
            }

            throw lastError || new Error('Unknown request error');
        },

        /**
         * Reads and processes the payload of an HTTP response.
         *
         * @param {Response} response The HTTP response object from which the payload is read.
         * @return {Promise<null|Object>} A Promise that resolves to the parsed payload as an object if the response content type is JSON,
         * or an object containing a textual `detail` if the content type is non-JSON. Returns null if the payload could not be read or parsed.
         */
        async readResponsePayload(response) {
            let data = null;
            const contentType = String(response.headers.get('content-type') || '');

            if (contentType.indexOf('application/json') !== -1) {
                try {
                    data = await response.json();
                } catch (e) {
                    data = null;
                }
            } else {
                try {
                    const text = await response.text();
                    data = text ? {detail: text} : null;
                } catch (e) {
                    data = null;
                }
            }

            return data;
        },

        /**
         * Calculates the delay time before the next retry based on the number of attempts.
         *
         * @param {number} attempt - The current retry attempt number.
         * @return {number} The calculated delay time in milliseconds for the next retry.
         */
        getRetryDelay(attempt) {
            return this.config.retryDelayMs * attempt;
        },

        /**
         * Pauses execution for a specified amount of time.
         *
         * @param {number} ms - The amount of time to wait, in milliseconds.
         * @return {Promise<void>} A promise that resolves after the specified time has elapsed.
         */
        wait(ms) {
            return new Promise((resolve) => {
                window.setTimeout(resolve, ms);
            });
        },

        /**
         * Constructs the full API URL by combining the base domain and the given path.
         *
         * @param {string} path - The relative path to append to the API's base domain. Leading slashes are removed automatically.
         * @return {string} The fully constructed API URL.
         */
        getApiUrl(path) {
            const base = String(DK_AI.api_domain || '').replace(/\/+$/, '');
            const suffix = String(path || '').replace(/^\/+/, '');
            return base + '/' + suffix;
        },

        /**
         * Determines if the given response object resembles a backend error.
         *
         * @param {Object} response - The response object to evaluate.
         * @return {boolean} Returns true if the response is null, not an object, or its 'answer' property is not a string; otherwise, returns false.
         */
        isBackendErrorLike(response) {
            return !response || typeof response !== 'object' || typeof response.answer !== 'string';
        },

        /**
         * Determines if the given response object qualifies as a lead handoff response.
         *
         * @param {Object} response - The response object to be evaluated.
         * @param {string} [response.mode] - The mode of the response, which may indicate lead handoff.
         * @param {string} [response.source] - The source of the response, potentially signifying lead handoff.
         * @param {string} [response.answer] - The answer field of the response, checked for lead handoff intent.
         * @return {boolean} Returns true if the response meets the criteria for a lead handoff, otherwise false.
         */
        isLeadHandoffResponse(response) {
            return Boolean(
                response &&
                (
                    response.mode === 'send_lead' ||
                    response.source === 'lead_handoff' ||
                    response.answer === 'send_lead'
                )
            );
        },

        /**
         * Determines if the provided response indicates an unsupported language.
         *
         * @param {Object} response - The response object to evaluate.
         * @param {string} response.source - The source of the response, expected to be 'guard'.
         * @param {string} response.lang - The language code provided in the response.
         * @return {boolean} Returns true if the language is unsupported or unknown; otherwise, returns false.
         */
        isUnsupportedLanguageResponse(response) {
            if (!response || response.source !== 'guard') {
                return false;
            }

            const lang = String(response.lang || '').trim().toLowerCase();

            if (!lang || lang === 'unknown') {
                return true;
            }

            return !Object.prototype.hasOwnProperty.call(DK_AI.i18n, lang);
        },

        /**
         * Evaluates whether the given response is an off-topic guard response.
         *
         * @param {Object} response - The response object to evaluate.
         * @param {string} response.source - The source of the response.
         * @returns {boolean} Returns true if the response is from the guard source and not an unsupported language response; otherwise, false.
         */
        isOffTopicGuardResponse(response) {
            return Boolean(
                response &&
                response.source === 'guard' &&
                !this.isUnsupportedLanguageResponse(response)
            );
        },

        /**
         * Determines whether the given response should be rendered as an error.
         *
         * @param {Object} response - The response object to evaluate.
         * @return {boolean} Returns true if the response should be rendered as an error, otherwise false.
         */
        shouldRenderResponseAsError(response) {
            if (this.isBackendErrorLike(response)) {
                return true;
            }

            if (!response || typeof response !== 'object') {
                return true;
            }

            if (response.source === 'blocked') {
                return true;
            }

            if (response.source === 'llm_fallback') {
                return true;
            }

            if (this.isUnsupportedLanguageResponse(response)) {
                return true;
            }

            if (this.isOffTopicGuardResponse(response)) {
                return false;
            }

            return false;
        },

        /**
         * Determines whether a conversation turn should be persisted based on the given response and user input.
         *
         * @param {Object} response - The response object from the system, which includes information such as the answer and source.
         * @param {string} userText - The input text from the user.
         * @return {boolean} Returns `true` if the conversation turn should be persisted, otherwise `false`.
         */
        shouldPersistConversationTurn(response, userText) {
            const cleanUserText = String(userText || '').trim();

            if (!cleanUserText) {
                return false;
            }

            if (!response || typeof response !== 'object') {
                return false;
            }

            if (typeof response.answer !== 'string' || !response.answer.trim()) {
                return false;
            }

            if (this.isLeadHandoffResponse(response)) {
                return false;
            }

            if (response.source === 'blocked') {
                return false;
            }

            if (response.source === 'llm_fallback') {
                return false;
            }

            if (this.isUnsupportedLanguageResponse(response)) {
                return false;
            }

            if (this.isBackendErrorLike(response)) {
                return false;
            }

            return response.source === 'cache' || response.source === 'llm' || response.source === 'rag_llm' || this.isOffTopicGuardResponse(response);
        },

        /**
         * Returns a user-friendly error message based on the provided error object.
         *
         * @param {Object} error - The error object containing details about the error.
         * @param {number} [error.status] - The HTTP status code associated with the error.
         * @param {Object} [error.payload] - The payload object containing additional error details.
         * @param {string} [error.payload.detail] - A detailed error message provided in the payload.
         * @param {string} [error.payload.answer] - An alternative error message provided in the payload.
         * @return {string} A localized or default error message string.
         */
        getChatErrorMessage(error) {
            if (error && error.status === 429) {
                return this.ts('chat_rate_limit');
            }

            if (error && error.status >= 500) {
                return this.ts('chat_unavailable');
            }

            const payload = error && error.payload ? error.payload : null;
            if (payload && typeof payload.detail === 'string' && payload.detail.trim()) {
                return payload.detail.trim();
            }
            if (payload && typeof payload.answer === 'string' && payload.answer.trim()) {
                return payload.answer.trim();
            }

            return this.ts('chat_generic_error');
        },

        /**
         * Appends latency metadata to the chat interface.
         *
         * @param {Object} response - The response object containing latency details.
         * @param {number} totalMs - The total latency in milliseconds for the request.
         * @return {void}
         */
        appendLatencyMeta(response, totalMs) {
            console.log('[AI Chat]: request latency:', this.formatSeconds(totalMs));

            const llmMs = response && typeof response.llm_latency_ms === 'number'
                ? response.llm_latency_ms
                : null;
            if (llmMs === null) {
                return;
            }

            $(this.selectors.messages).append(
                '<div class="ai-chat__meta ai-chat__meta--center">' +
                '<span class="ai-chat__meta-text">LLM: ' + this.escapeHtml(this.formatSeconds(llmMs)) + '</span>' +
                '</div>'
            );

            this.scrollMessagesToBottom();
        },

        /**
         * Converts a given time in milliseconds to a string representing seconds, rounded to one decimal place.
         * If the input is not a valid number or is less than zero, it defaults to zero.
         *
         * @param {number} ms - The time duration in milliseconds to be formatted.
         * @return {string} The formatted time in seconds with one decimal precision followed by an 's'.
         */
        formatSeconds(ms) {
            return (Math.max(0, Number(ms || 0)) / 1000).toFixed(1) + 's';
        },

        /**
         * Appends a chat message to the message container.
         *
         * @param {Object} data - The data object containing message details.
         * @param {string} [data.role='assistant'] - The role of the message sender (e.g., 'user', 'assistant', 'error').
         * @param {string} [data.speaker=''] - The name or identifier of the message speaker.
         * @param {string} [data.text=''] - The text content of the message.
         * @param {boolean} [data.pending=false] - Indicates if the message is in a pending state.
         * @param {boolean} [data.loading=false] - Indicates if the message content is still loading.
         * @return {string} The unique ID of the newly appended message.
         */
        appendMessage(data = {}) {
            const role = String(data.role || 'assistant');
            const speaker = String(data.speaker || '');
            const text = String(data.text || '');
            const pending = Boolean(data.pending);
            const loading = Boolean(data.loading);
            const messageId = 'msg-' + Date.now() + '-' + Math.random().toString(16).slice(2);

            const roleClass = role === 'user'
                ? 'ai-chat__message--user'
                : role === 'error'
                    ? 'ai-chat__message--error'
                    : 'ai-chat__message--assistant';

            const contentHtml = loading
                ? this.getLoadingMessageHtml()
                : this.formatMessageHtml(text);

            $(this.selectors.messages).append(
                '<div class="ai-chat__message ' + roleClass + (pending ? ' is-pending' : '') + '" data-message-id="' + this.escapeHtml(messageId) + '">' +
                '<div class="ai-chat__bubble">' +
                this.getMessageSpeakerHtml(role, speaker) +
                '<div class="ai-chat__content">' + contentHtml + '</div>' +
                '</div>' +
                '</div>'
            );

            this.scrollMessagesToBottom();
            return messageId;
        },

        /**
         * Replaces the loading message for the assistant with updated content or appends a new message if the specified message does not exist.
         *
         * @param {string} messageId - The unique identifier of the message to be replaced.
         * @param {Object} [data={}] - Optional data object containing message details such as `role`, `speaker`, and `text`.
         * @param {string} [data.role='assistant'] - The role of the message (e.g., 'assistant', 'user', or 'error').
         * @param {string} [data.speaker=''] - The speaker's name or identifier, if applicable.
         * @param {string} [data.text=''] - The text content of the message.
         * @return {void}
         */
        replaceAssistantLoadingMessage(messageId, data = {}) {
            const $message = $(this.selectors.messages).find('[data-message-id="' + messageId + '"]');

            if (!$message.length) {
                this.appendMessage(data);
                return;
            }

            const role = String(data.role || 'assistant');
            const speaker = String(data.speaker || '');
            const text = String(data.text || '');

            $message.removeClass('is-pending ai-chat__message--assistant ai-chat__message--user ai-chat__message--error');
            $message.addClass(
                role === 'user'
                    ? 'ai-chat__message--user'
                    : role === 'error'
                        ? 'ai-chat__message--error'
                        : 'ai-chat__message--assistant'
            );

            $message.find('.ai-chat__bubble').html(
                this.getMessageSpeakerHtml(role, speaker) +
                '<div class="ai-chat__content">' + this.formatMessageHtml(text) + '</div>'
            );

            this.scrollMessagesToBottom();
        },

        /**
         * Generates the HTML string for the speaker section of a chat message based on the role and speaker's name.
         *
         * @param {string} role - The role of the speaker (e.g., 'error', 'assistant').
         * @param {string} speaker - The name of the speaker. Optional, defaults to a localized value if not provided.
         * @return {string} The HTML string representing the speaker section of the chat message.
         *                  Returns an empty string if the role is not recognized.
         */
        getMessageSpeakerHtml(role, speaker) {
            if (role === 'error') {
                return '<div class="ai-chat__speaker"><span class="ai-chat__speaker-name">' + this.escapeHtml(this.tl('error')) + '</span></div>';
            }

            if (role === 'assistant') {
                return '<div class="ai-chat__speaker ai-chat__speaker--assistant"><span class="ai-chat__speaker-name">' + this.escapeHtml(speaker || this.t('assistant_name')) + '</span></div>';
            }

            return '';
        },

        /**
         * Generates and returns the HTML string for a loading message.
         *
         * @return {string} The HTML markup for the loading message, including a div with a loading icon and an ARIA label.
         */
        getLoadingMessageHtml() {
            return '<div class="ai-chat__loading" aria-label="Loading">' + this.icons.loading + '</div>';
        },

        /**
         * Marks a message as sent by removing the 'is-pending' class from the message with the specified ID.
         *
         * @param {string} messageId - The unique identifier of the message to mark as sent.
         * @return {void} This method does not return a value.
         */
        markMessageAsSent(messageId) {
            $(this.selectors.messages)
                .find('[data-message-id="' + messageId + '"]')
                .removeClass('is-pending');
        },

        /**
         * Removes a message element from the DOM based on the given message ID.
         *
         * @param {string} messageId The unique identifier of the message to be removed.
         * @return {void} This method does not return a value.
         */
        removeMessageById(messageId) {
            $(this.selectors.messages).find('[data-message-id="' + messageId + '"]').remove();
        },

        /**
         * Scrolls the messages container to the bottom, ensuring the most recent messages are visible.
         *
         * @return {void} This method does not return a value.
         */
        scrollMessagesToBottom() {
            const el = $(this.selectors.messages).get(0);

            if (!el) {
                return;
            }

            window.requestAnimationFrame(() => {
                el.scrollTop = el.scrollHeight;
            });
        },

        /**
         * Sets the composer state to busy or not busy by enabling or disabling the input,
         * submit, and quick button elements. It also updates the `aria-busy` attribute
         * on the submit button for accessibility purposes.
         *
         * @param {boolean} isBusy - Indicates whether the composer should be marked as busy. A truthy value disables the elements, while a falsy value enables them.
         * @return {void}
         */
        setComposerBusy(isBusy) {
            const busy = Boolean(isBusy);

            $(this.selectors.input).prop('disabled', busy);
            $(this.selectors.submit).prop('disabled', busy);
            $(this.selectors.quickBtn).prop('disabled', busy);

            if (busy) {
                $(this.selectors.submit).attr('aria-busy', 'true');
            } else {
                $(this.selectors.submit).removeAttr('aria-busy');
            }
        },

        /**
         * Normalizes broken bracket links within a given text by replacing malformed bracketed URLs
         * with their correct usable form.
         *
         * @param {string} text - The input text that may contain broken bracket links.
         * @return {string} The processed text with broken bracket links corrected.
         */
        normalizeBrokenBracketLinks(text) {
            let output = String(text || '');

            output = output.replace(/\[(https?:\/\/[^\]]+)\](https?:\/\/[^\s)]+)/gi, '$2');
            output = output.replace(/\[(www\.[^\]]+)\](https?:\/\/[^\s)]+)/gi, '$2');

            return output;
        },

        /**
         * Formats a message text into HTML content. Converts text with newline breaks, lists,
         * and other formatting into properly structured HTML, suitable for displaying in a web interface.
         *
         * @param {string} text The input text to be formatted as HTML. The text may include
         *                      newline breaks and basic list formatting with `-` or `*` at the start of list items.
         * @return {string} The formatted HTML string based on the input text.
         */
        formatMessageHtml(text) {
            const normalized = this.normalizeBrokenBracketLinks(text)
                .replace(/\r\n/g, '\n')
                .replace(/\r/g, '\n');

            const lines = normalized.split('\n');
            const blocks = [];
            let listBuffer = [];

            const flushList = () => {
                if (!listBuffer.length) {
                    return;
                }

                blocks.push(
                    '<ul class="ai-chat__list">' +
                    listBuffer.map((item) => '<li>' + this.formatInlineText(item) + '</li>').join('') +
                    '</ul>'
                );

                listBuffer = [];
            };

            lines.forEach((line) => {
                const trimmed = line.trim();

                if (!trimmed.length) {
                    flushList();
                    return;
                }

                if (/^[-*]\s+/.test(trimmed)) {
                    listBuffer.push(trimmed.replace(/^[-*]\s+/, ''));
                    return;
                }

                flushList();
                blocks.push('<p>' + this.formatInlineText(trimmed) + '</p>');
            });

            flushList();

            return blocks.join('');
        },

        /**
         * Formats the provided text by converting specific patterns into HTML elements.
         * Supports converting markdown-like links, bold, and italic text into their corresponding HTML representations.
         *
         * @param {string} text - The input text to be formatted. Can include markdown-like links, bold, or italic patterns.
         * @return {string} The formatted text containing HTML elements where applicable.
         */
        formatInlineText(text) {
            let html = this.escapeHtml(String(text || ''));

            html = html.replace(
                /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
                '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
            );

            html = html.replace(
                /(^|[\s(])((?:https?:\/\/|www\.)[^\s<]+)/g,
                (match, prefix, url) => {
                    let href = url;
                    if (!/^https?:\/\//i.test(href)) {
                        href = 'https://' + href;
                    }

                    return prefix + '<a href="' + this.escapeHtml(href) + '" target="_blank" rel="noopener noreferrer">' + this.escapeHtml(url) + '</a>';
                }
            );

            html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
            html = html.replace(/__([^_]+)__/g, '<strong>$1</strong>');
            html = html.replace(/(^|[\s(])\*([^*\n]+)\*(?=[\s.,!?;:)]|$)/g, '$1<em>$2</em>');
            html = html.replace(/(^|[\s(])_([^_\n]+)_(?=[\s.,!?;:)]|$)/g, '$1<em>$2</em>');

            return html;
        },

        /**
         * Generates the HTML string for the introductory assistant message.
         *
         * The method returns a formatted HTML snippet for displaying the assistant's
         * introductory message, including a title and message text. The content is
         * localized and properly escaped to ensure security and correct formatting.
         *
         * @return {string} The HTML string for the assistant's introductory message.
         */
        getIntroMessageHtml() {
            return '' +
                '<div class="ai-chat__message ai-chat__message--assistant ai-chat__message--intro">' +
                '<div class="ai-chat__bubble">' +
                '<p class="ai-chat__intro-title js-ai-chat-intro-title">' + this.escapeHtml(this.t('assistant_name')) + '</p>' +
                '<p class="ai-chat__intro-text js-ai-chat-intro-text">' + this.escapeHtml(this.t('assistant_start_message')) + '</p>' +
                '</div>' +
                '</div>';
        },

        /**
         * Escapes special characters in a string to their corresponding HTML entities.
         *
         * @param {string} value - The input string to be escaped.
         * @return {string} The escaped string with special characters replaced by HTML entities.
         */
        escapeHtml(value) {
            return String(value)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;');
        },

        /**
         * Opens the service status section by updating DOM attributes and internal state.
         * It makes the service status visible, updates the root element's state to 'error',
         * and adjusts the component's state accordingly.
         *
         * @return {void} This method does not return a value.
         */
        openServiceStatus() {
            if (this.state.isOpen) {
                this.unlockMobileBodyScroll();
            }

            $(this.selectors.status).prop('hidden', false);
            $(this.selectors.root).attr('data-state', 'error');

            this.state.isServiceStatusOpen = true;
            this.state.isOpen = false;
        },

        /**
         * Closes the service status by updating the relevant DOM elements and state.
         *
         * @return {void} This method does not return a value.
         */
        closeServiceStatus() {
            const $root = $(this.selectors.root);

            $(this.selectors.status).prop('hidden', true);

            if (this.state.isOpen) {
                $root.attr('data-state', 'open');
            } else if ($root.attr('data-state') === 'error') {
                $root.attr('data-state', 'closed');
            }

            this.state.isServiceStatusOpen = false;
        },

        /**
         * Sets the loading state for the health check process.
         *
         * @param {boolean} isLoading Indicates whether the health check is currently loading.
         * @return {void}
         */
        setHealthCheckLoading(isLoading) {
            this.state.isCheckingHealth = Boolean(isLoading);

            $(this.selectors.statusRetry).prop('disabled', this.state.isCheckingHealth);

            this.buildAnimatedButtonCaption(
                $(this.selectors.statusRetry).find('.btn-caption'),
                this.state.isCheckingHealth ? this.ts('checking') : this.ts('unavailable_retry')
            );
        },

        /**
         * Restores the session ID from the local storage and updates the application's state.
         * If a valid session ID is found in local storage, it is assigned to the state's sessionId.
         * Logs an error message to the console if the restoration process fails.
         *
         * @return {void} Does not return a value.
         */
        restoreSessionId() {
            try {
                const stored = window.localStorage.getItem(this.config.storageSessionKey);

                if (stored && String(stored).trim()) {
                    this.state.sessionId = String(stored).trim();
                }
            } catch (error) {
                console.log('[AI Chat]: session restore failed:', error);
            }
        },

        /**
         * Persists the session ID by storing it in local storage and updating the state.
         *
         * @param {string|null|undefined} sessionId - The session ID to be persisted. If null or undefined, the session ID will be cleared.
         * @return {void} This method does not return a value.
         */
        persistSessionId(sessionId) {
            const value = String(sessionId || '').trim();

            this.state.sessionId = value || null;

            try {
                if (value) {
                    window.localStorage.setItem(this.config.storageSessionKey, value);
                } else {
                    window.localStorage.removeItem(this.config.storageSessionKey);
                }
            } catch (error) {
                console.log('[AI Chat]: session persist failed:', error);
            }
        },

        /**
         * Clears the current session ID by setting it to null and removes the associated session key from local storage.
         * Logs an error message to the console if the operation fails.
         *
         * @return {void} Does not return a value.
         */
        clearSessionId() {
            this.state.sessionId = null;

            try {
                window.localStorage.removeItem(this.config.storageSessionKey);
            } catch (error) {
                console.log('[AI Chat]: session clear failed:', error);
            }
        },

        /**
         * Restores the lead state from localStorage based on the specified storage key.
         * The restored state updates the `isLeadMode` and `leadData` properties of the `state` object
         * if valid data is found in localStorage. If the stored data is invalid or an error occurs during
         * processing, no changes are made to the state.
         *
         * @return {void} This method does not return a value.
         */
        restoreLeadState() {
            try {
                const raw = window.localStorage.getItem(this.config.storageLeadStateKey);
                if (!raw) {
                    return;
                }

                const parsed = JSON.parse(raw);
                if (!parsed || typeof parsed !== 'object') {
                    return;
                }

                this.state.isLeadMode = Boolean(parsed.isLeadMode);
                this.state.leadData = parsed.leadData && typeof parsed.leadData === 'object'
                    ? parsed.leadData
                    : null;
            } catch (error) {
                console.log('[AI Chat]: lead state restore failed:', error);
            }
        },

        /**
         * Persists the lead state to local storage by saving or removing the lead-related data
         * based on the current state of the application.
         *
         * The method checks if the application is in lead mode or if lead data exists. If neither
         * condition is met, it clears the stored lead state from local storage. Otherwise, it
         * stores the serialized lead data.
         *
         * @return {void} Does not return any value.
         */
        persistLeadState() {
            try {
                const payload = {
                    isLeadMode: Boolean(this.state.isLeadMode),
                    leadData: this.state.leadData || null,
                };

                if (!payload.isLeadMode && !payload.leadData) {
                    window.localStorage.removeItem(this.config.storageLeadStateKey);
                    return;
                }

                window.localStorage.setItem(this.config.storageLeadStateKey, JSON.stringify(payload));
            } catch (error) {
                console.log('[AI Chat]: lead state persist failed:', error);
            }
        },

        /**
         * Resets the lead-related state and removes any persisted lead data from local storage.
         *
         * The method clears the `leadData` and `isLeadMode` properties in the component's state.
         * It also attempts to remove the lead state data from the browser's local storage
         * using the key specified in `this.config.storageLeadStateKey`.
         *
         * If an error occurs while removing the data from local storage, it logs the error to the console.
         *
         * @return {void} Does not return a value.
         */
        clearLeadState() {
            this.state.leadData = null;
            this.state.isLeadMode = false;

            try {
                window.localStorage.removeItem(this.config.storageLeadStateKey);
            } catch (error) {
                console.log('[AI Chat]: lead state clear failed:', error);
            }
        },

        /**
         * Retrieves stored messages from the browser's local storage.
         * The messages are validated to ensure they are an array of objects
         * containing a valid `role` ('user' or 'assistant') and a non-empty
         * string `text` property.
         *
         * @return {Array} An array of validated message objects. If no messages
         * exist or an error occurs during retrieval or parsing, an empty array is returned.
         */
        getStoredMessages() {
            try {
                const raw = window.localStorage.getItem(this.config.storageMessagesKey);
                if (!raw) {
                    return [];
                }

                const parsed = JSON.parse(raw);
                if (!Array.isArray(parsed)) {
                    return [];
                }

                return parsed.filter((item) => {
                    return item &&
                        (item.role === 'user' || item.role === 'assistant') &&
                        typeof item.text === 'string' &&
                        item.text.trim().length > 0;
                });
            } catch (error) {
                console.log('[AI Chat]: messages restore failed:', error);
                return [];
            }
        },

        /**
         * Stores an array of messages in the local storage or removes the stored messages if the input is invalid or empty.
         *
         * @param {Array} messages - The array of messages to store in local storage. If the parameter is not an array or is empty, the stored messages are removed.
         * @return {void} Does not return a value.
         */
        setStoredMessages(messages) {
            try {
                if (!Array.isArray(messages) || !messages.length) {
                    window.localStorage.removeItem(this.config.storageMessagesKey);
                    return;
                }

                window.localStorage.setItem(this.config.storageMessagesKey, JSON.stringify(messages));
            } catch (error) {
                console.log('[AI Chat]: messages persist failed:', error);
            }
        },

        /**
         * Clears all stored messages from local storage using the configured storage key.
         *
         * @return {void} Does not return any value.
         */
        clearStoredMessages() {
            try {
                window.localStorage.removeItem(this.config.storageMessagesKey);
            } catch (error) {
                console.log('[AI Chat]: messages clear failed:', error);
            }
        },

        /**
         * Saves a single turn of a conversation consisting of user and assistant messages.
         *
         * @param {string} userText - The text input provided by the user.
         * @param {string} assistantText - The text response generated by the assistant.
         * @return {void} This method does not return a value.
         */
        persistConversationTurn(userText, assistantText) {
            const cleanUser = String(userText || '').trim();
            const cleanAssistant = String(assistantText || '').trim();

            if (!cleanUser || !cleanAssistant) {
                return;
            }

            const messages = this.getStoredMessages();

            messages.push({
                role: 'user',
                text: cleanUser,
            });

            messages.push({
                role: 'assistant',
                text: cleanAssistant,
            });

            this.setStoredMessages(messages);
        },

        /**
         * Hydrates stored messages into the chat interface if the message history has not
         * already been hydrated. This method checks for stored messages, evaluates the
         * chat's existing content, appends messages as appropriate, and updates relevant
         * state indicators.
         *
         * @return {void} No return value.
         */
        hydrateStoredMessages() {
            if (this.state.hasHydratedHistory) {
                return;
            }

            const messages = this.getStoredMessages();
            if (!messages.length) {
                this.state.hasHydratedHistory = true;
                return;
            }

            const $messages = $(this.selectors.messages);
            const hasOnlyIntro = $messages.children('.ai-chat__message').length === 1 &&
                $messages.find('.ai-chat__message--intro').length === 1;

            if (!hasOnlyIntro) {
                this.state.hasHydratedHistory = true;
                return;
            }

            messages.forEach((item) => {
                this.appendMessage({
                    role: item.role,
                    speaker: item.role === 'assistant' ? this.t('assistant_name') : '',
                    text: item.text,
                });
            });

            this.state.hasTyped = true;
            $(this.selectors.consent).hide();
            this.hideQuickQuestions();
            this.state.hasHydratedHistory = true;

            this.scrollMessagesToBottom();
        },

        /**
         * Updates the UI to reflect the lead mode state.
         *
         * @param {boolean} isLeadMode - A boolean indicating whether lead mode is active.
         * @return {void} This method does not return a value.
         */
        setLeadModeUi(isLeadMode) {
            this.state.isLeadMode = Boolean(isLeadMode);

            if (this.state.isLeadMode) {
                $(this.selectors.form).prop('hidden', true).hide();
                $(this.selectors.warning).html(this.t('contact_consent'));
                this.hideQuickQuestions();
            } else {
                $(this.selectors.form).prop('hidden', false).show();

                if (this.state.leadSuccessMessage) {
                    $(this.selectors.warning).text(this.state.leadSuccessMessage);
                } else {
                    $(this.selectors.warning).html(this.t('warning'));
                }
            }
        },

        /**
         * Renders a lead form element inside the specified container if it does not already exist.
         * The lead form includes fields for name, email, phone, and company, along with error handling
         * and a CAPTCHA element. After rendering, it updates localized texts and scrolls the container
         * to the bottom to ensure visibility.
         *
         * @return {void} No return value.
         */
        renderLeadForm() {
            const existing = $(this.selectors.messages).find(this.selectors.leadForm);
            if (existing.length) {
                return;
            }

            const html =
                '<div class="ai-chat__message ai-chat__message--assistant ai-chat__message--lead">' +
                '<div class="ai-chat__bubble ai-chat__lead">' +
                '<form class="ai-chat__lead-form js-ai-chat-lead-form" novalidate>' +
                '<div class="ai-chat__lead-grid">' +
                '<div class="ai-chat__lead-field">' +
                '<label for="ai-chat-lead-name">' + this.escapeHtml(this.tc('name')) + '</label>' +
                '<input id="ai-chat-lead-name" name="name" type="text" placeholder="' + this.escapeHtml(this.tc('name_placeholder')) + '" />' +
                '</div>' +
                '<div class="ai-chat__lead-field">' +
                '<label for="ai-chat-lead-email">' + this.escapeHtml(this.tc('email')) + '</label>' +
                '<input id="ai-chat-lead-email" name="email" type="email" placeholder="' + this.escapeHtml(this.tc('email_placeholder')) + '" />' +
                '</div>' +
                '<div class="ai-chat__lead-field">' +
                '<label for="ai-chat-lead-phone">' + this.escapeHtml(this.tc('phone')) + '</label>' +
                '<input id="ai-chat-lead-phone" name="phone" type="text" placeholder="' + this.escapeHtml(this.tc('phone_placeholder')) + '" />' +
                '</div>' +
                '<div class="ai-chat__lead-field">' +
                '<label for="ai-chat-lead-company">' + this.escapeHtml(this.tc('company')) + '</label>' +
                '<input id="ai-chat-lead-company" name="company" type="text" placeholder="' + this.escapeHtml(this.tc('company_placeholder')) + '" />' +
                '</div>' +
                '</div>' +
                '<div class="ai-chat__lead-error" role="alert" aria-live="polite" hidden></div>' +
                '<div class="h-captcha" data-size="invisible"></div>' +
                '<div class="ai-chat__lead-actions">' +
                '<button type="submit" class="ai-chat__lead-submit js-ai-chat-lead-submit btn btn-anim btn-small btn-accent">' +
                '<span class="btn-caption">' + this.escapeHtml(this.tc('submit')) + '</span>' +
                '</button>' +
                '</div>' +
                '</form>' +
                '</div>' +
                '</div>';

            $(this.selectors.messages).append(html);
            this.refreshLeadFormTexts();
            this.scrollMessagesToBottom();
        },

        /**
         * Updates the text content and placeholder attributes of the lead form fields
         * and refreshes their captions to match the current translations.
         *
         * @return {void} This method does not return a value.
         */
        refreshLeadFormTexts() {
            const $form = $(this.selectors.messages).find(this.selectors.leadForm);
            if (!$form.length) {
                return;
            }

            $form.find('label[for="ai-chat-lead-name"]').text(this.tc('name'));
            $form.find('#ai-chat-lead-name').attr('placeholder', this.tc('name_placeholder'));

            $form.find('label[for="ai-chat-lead-email"]').text(this.tc('email'));
            $form.find('#ai-chat-lead-email').attr('placeholder', this.tc('email_placeholder'));

            $form.find('label[for="ai-chat-lead-phone"]').text(this.tc('phone'));
            $form.find('#ai-chat-lead-phone').attr('placeholder', this.tc('phone_placeholder'));

            $form.find('label[for="ai-chat-lead-company"]').text(this.tc('company'));
            $form.find('#ai-chat-lead-company').attr('placeholder', this.tc('company_placeholder'));

            this.buildAnimatedButtonCaption(
                $form.find('.btn-caption'),
                this.tc('submit')
            );
        },

        /**
         * Retrieves the lead error box element within the provided form.
         * If the error box does not exist, it creates a new error box element
         * and inserts it into the form before the lead actions element.
         *
         * @param {jQuery} $form - The jQuery object representing the form containing the lead actions and error box.
         * @return {jQuery} - The jQuery object representing the lead error box element.
         */
        getLeadErrorBox($form) {
            let $box = $form.find('.ai-chat__lead-error');

            if (!$box.length) {
                $box = $('<div class="ai-chat__lead-error" role="alert" aria-live="polite" hidden></div>');
                $form.find('.ai-chat__lead-actions').before($box);
            }

            return $box;
        },

        /**
         * Displays an error message in the lead error box associated with the given form.
         *
         * @param {Object} $form - The jQuery object representing the form element.
         * @param {string} [msg] - The error message to display. If not provided, a default error message will be shown.
         * @return {void}
         */
        showLeadError($form, msg) {
            const $box = this.getLeadErrorBox($form);
            $box.text(msg || this.tc('error')).prop('hidden', false);
        },

        /**
         * Hides the lead error message for a specified form by setting the error box's hidden property to true and clearing its text content.
         *
         * @param {jQuery} $form - The jQuery object representing the form whose lead error should be hidden.
         * @return {void} This method does not return a value.
         */
        hideLeadError($form) {
            const $box = this.getLeadErrorBox($form);
            $box.prop('hidden', true).text('');
        },

        /**
         * Renders a success message for the lead in the chat interface.
         *
         * @param {string} message - The success message to display. If not provided, a default success message will be used.
         * @return {void} This method does not return a value.
         */
        renderLeadSuccess(message) {
            const successText = String(message || this.tc('success')).trim();
            const $leadWrap = $(this.selectors.messages).find('.ai-chat__message--lead');

            if (!$leadWrap.length) {
                return;
            }

            $leadWrap.html(
                '<div class="ai-chat__bubble ai-chat__lead ai-chat__lead-success">' +
                '<div class="ai-chat__speaker ai-chat__speaker--assistant">' +
                '<span class="ai-chat__speaker-name">' + this.escapeHtml(this.t('assistant_name')) + '</span>' +
                '</div>' +
                '<div class="ai-chat__content"><p>' + this.escapeHtml(successText) + '</p></div>' +
                '</div>'
            );

            this.scrollMessagesToBottom();
        },

        /**
         * Validates the lead form fields and ensures that they meet the required criteria.
         *
         * @param {jQuery} $form - The jQuery object representing the form to validate.
         * @return {boolean} Returns true if the form is valid; otherwise, false.
         */
        validateLeadForm($form) {
            const name = String($form.find('input[name="name"]').val() || '').trim();
            const email = String($form.find('input[name="email"]').val() || '').trim();

            if (!name) {
                this.showLeadError($form, this.tc('name_required'));
                return false;
            }

            if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                this.showLeadError($form, this.tc('email_invalid'));
                return false;
            }

            this.hideLeadError($form);
            return true;
        },

        /**
         * Dynamically loads the hCaptcha script if it has not already been loaded.
         *
         * If the script is currently being loaded, it returns the existing Promise.
         * Once the script is successfully loaded, subsequent calls to this method will
         * resolve immediately.
         *
         * @return {Promise<void>} A Promise that resolves when the hCaptcha script is successfully loaded
         * or rejects if the script fails to load.
         */
        loadHCaptcha() {
            if (window.hcaptcha) {
                return Promise.resolve();
            }

            if (window.__dkAiHcaptchaLoading) {
                return window.__dkAiHcaptchaLoading;
            }

            window.__dkAiHcaptchaLoading = new Promise((resolve, reject) => {
                const s = document.createElement('script');
                s.src = 'https://js.hcaptcha.com/1/api.js?render=explicit';
                s.async = true;
                s.defer = true;
                s.onload = function () {
                    resolve();
                };
                s.onerror = function () {
                    reject(new Error('hCaptcha failed to load'));
                };
                document.head.appendChild(s);
            });

            return window.__dkAiHcaptchaLoading;
        },

        /**
         * Ensures that an hCaptcha widget is properly initialized and associated with the given form.
         * If the widget already exists for the form, its ID is returned. Otherwise, the widget is created,
         * initialized, and its ID is stored in the form's data attributes.
         *
         * @param {jQuery} $form The jQuery object representing the form for which the hCaptcha widget should be ensured.
         * @return {number|null} The ID of the hCaptcha widget if successfully rendered, or null if hCaptcha is not available or initialization fails.
         */
        ensureLeadHCaptchaWidget($form) {
            let id = $form.data('hcaptcha-id');
            if (typeof id === 'number') {
                return id;
            }

            let container = $form.find('.h-captcha')[0];
            if (!container) {
                container = document.createElement('div');
                container.className = 'h-captcha';
                container.setAttribute('data-size', 'invisible');
                $form.append(container);
            }

            if (!window.hcaptcha || !window.DK_AI || !DK_AI.hcaptcha_sitekey) {
                return null;
            }

            id = hcaptcha.render(container, {
                sitekey: DK_AI.hcaptcha_sitekey,
                size: 'invisible',
                callback: (token) => {
                    this.submitLeadWithCaptcha($form, token);
                },
                'error-callback': () => {
                    this.showLeadError($form, this.tc('captcha_failed'));
                    $form.find('[type="submit"]').prop('disabled', false);
                },
                'expired-callback': () => {
                    this.showLeadError($form, this.tc('captcha_failed'));
                    $form.find('[type="submit"]').prop('disabled', false);
                }
            });

            $form.data('hcaptcha-id', id);
            return id;
        },

        /**
         * Builds a payload string representing structured data from lead information and submitted form data.
         *
         * @param {Object} formData - Submission form data containing user-inputted information.
         * @param {string} [formData.name] - The name of the person submitting the form.
         * @param {string} [formData.email] - The email address of the person submitting the form.
         * @param {string} [formData.phone] - The phone number of the person submitting the form.
         * @param {string} [formData.company] - The company name provided by the person submitting the form.
         * @return {string} A formatted string containing the lead data and submitted form data, where each block is separated by a newline.
         */
        buildLeadMessagePayload(formData = {}) {
            const lead = this.state.leadData || {};
            const lines = [];

            const pushBlock = (label, value) => {
                const clean = String(value || '').trim();
                if (!clean) {
                    return;
                }

                lines.push(label + ':');
                lines.push(clean);
                lines.push('');
            };

            pushBlock('Summary for Dmytro', lead.summary_for_dmytro);
            pushBlock('Project goal', lead.project_goal);

            if (Array.isArray(lead.requested_help) && lead.requested_help.length) {
                lines.push('Requested help:');
                lead.requested_help.forEach((item) => {
                    const clean = String(item || '').trim();
                    if (clean) {
                        lines.push('- ' + clean);
                    }
                });
                lines.push('');
            }

            pushBlock('Technical context', lead.technical_context);
            pushBlock('Constraints', lead.constraints);
            pushBlock('User intent', lead.user_intent);
            pushBlock('Suggested subject', lead.suggested_subject);

            pushBlock('Submitted name', formData.name);
            pushBlock('Submitted email', formData.email);
            pushBlock('Submitted phone', formData.phone);
            pushBlock('Submitted company', formData.company);

            return lines.join('\n').trim();
        },

        /**
         * Handles the submission process for the lead form, including form validation,
         * disabling the submit button, loading hCaptcha, and handling hCaptcha execution.
         *
         * @param {jQuery} $form - The jQuery object representing the lead form element to be submitted.
         * @return {Promise<void>} Resolves when the submission processing is complete or terminates early if conditions are not met.
         */
        async handleLeadFormSubmit($form) {
            if (!$form || !$form.length) {
                return;
            }

            if (!this.validateLeadForm($form)) {
                return;
            }

            this.hideLeadError($form);
            $form.find('[type="submit"]').prop('disabled', true);

            try {
                await this.loadHCaptcha();

                const widgetId = this.ensureLeadHCaptchaWidget($form);
                if (widgetId === null) {
                    this.showLeadError($form, this.tc('captcha_failed'));
                    $form.find('[type="submit"]').prop('disabled', false);
                    return;
                }

                hcaptcha.execute(widgetId);
            } catch (error) {
                this.showLeadError($form, this.tc('captcha_failed'));
                $form.find('[type="submit"]').prop('disabled', false);
            }
        },

        /**
         * Submits a lead form with CAPTCHA validation and sends the data via an AJAX request.
         *
         * @param {jQuery} $form The jQuery object representing the form to be submitted. It should contain input fields for name, email, phone, and company.
         * @param {string} token The hCaptcha token generated for the user to verify CAPTCHA validation.
         * @return {void} This method does not return a value directly. The response from the server is handled asynchronously.
         */
        submitLeadWithCaptcha($form, token) {
            const formData = {
                name: String($form.find('input[name="name"]').val() || '').trim(),
                email: String($form.find('input[name="email"]').val() || '').trim(),
                phone: String($form.find('input[name="phone"]').val() || '').trim(),
                company: String($form.find('input[name="company"]').val() || '').trim(),
            };

            const lead = this.state.leadData || {};
            const payload = {
                action: 'dk_contact',
                'h-captcha-response': token,
                project_name: formData.company || '',
                admin_email: '',
                form_subject: String(lead.suggested_subject || 'AI Assistant Lead').trim(),
                name: formData.name,
                company: formData.company,
                email: formData.email,
                phone: formData.phone,
                message: this.buildLeadMessagePayload(formData),
            };

            $.ajax({
                url: (window.DK_AI && DK_AI.ajax_url) ? DK_AI.ajax_url : '/wp-admin/admin-ajax.php',
                method: 'POST',
                dataType: 'json',
                timeout: 15000,
                data: payload
            })
                .done((resp) => {
                    const result = (resp && resp.result) ? resp.result : 'error';
                    const errorKey = (resp && resp.error_code) ? resp.error_code : 'response_error';

                    this.handleLeadSubmitResponse({result, errorKey}, $form);
                })
                .fail((xhr, status) => {
                    let errorKey = 'network_error';

                    if (status === 'timeout') {
                        errorKey = 'timeout_error';
                    }

                    this.handleLeadSubmitResponse({result: 'error', errorKey}, $form);
                })
                .always(() => {
                    const wid = $form.data('hcaptcha-id');
                    if (typeof wid === 'number' && window.hcaptcha) {
                        hcaptcha.reset(wid);
                    }

                    $form.find('[type="submit"]').prop('disabled', false);
                });
        },

        /**
         * Handles the response from a lead submission process.
         *
         * @param {Object} resp - The response object returned from the lead submission API or process.
         * @param {Object} $form - The jQuery-wrapped form element associated with the lead submission.
         * @return {void}
         */
        handleLeadSubmitResponse(resp, $form) {
            if (resp && resp.result === 'success') {
                const successMessage = this.tc('success');

                this.hideLeadError($form);
                this.renderLeadSuccess(successMessage);

                this.clearSessionId();
                this.clearStoredMessages();
                this.clearLeadState();
                this.clearLeadFormData();
                this.state.hasHydratedHistory = false;
                this.state.leadSuccessMessage = String(successMessage);

                $(this.selectors.warning).text(successMessage);
                return;
            }

            const errorKey = (resp && resp.errorKey) ? resp.errorKey : 'response_error';
            this.showLeadError($form, this.tc(errorKey));
        },

        /**
         * Retrieves and parses the stored lead form data from local storage.
         * If the data is missing, invalid, or not in the expected format, it returns null.
         * The returned object includes sanitized fields for name, email, phone, and company.
         *
         * @return {Object|null} An object with the lead form data (name, email, phone, company) if available and valid.
         *                       Returns null if no valid data exists or an error occurs during parsing.
         */
        getStoredLeadFormData() {
            try {
                const raw = window.localStorage.getItem(this.config.storageLeadFormKey);
                if (!raw) {
                    return null;
                }

                const parsed = JSON.parse(raw);
                if (!parsed || typeof parsed !== 'object') {
                    return null;
                }

                return {
                    name: String(parsed.name || '').trim(),
                    email: String(parsed.email || '').trim(),
                    phone: String(parsed.phone || '').trim(),
                    company: String(parsed.company || '').trim(),
                };
            } catch (error) {
                console.log('[AI Chat]: lead form restore failed:', error);
                return null;
            }
        },

        /**
         * Persists the lead form data to local storage if at least one of the form fields has a value.
         * Removes the data from local storage if all fields are empty.
         *
         * @param {Object} formData - An object containing form data with potential fields: name, email, phone, and company.
         * @param {string} [formData.name] - The name input from the form.
         * @param {string} [formData.email] - The email input from the form.
         * @param {string} [formData.phone] - The phone input from the form.
         * @param {string} [formData.company] - The company input from the form.
         * @return {void} This method does not return a value.
         */
        persistLeadFormData(formData = {}) {
            try {
                const payload = {
                    name: String(formData.name || '').trim(),
                    email: String(formData.email || '').trim(),
                    phone: String(formData.phone || '').trim(),
                    company: String(formData.company || '').trim(),
                };

                const hasAnyValue = Object.values(payload).some((value) => value.length > 0);

                if (!hasAnyValue) {
                    window.localStorage.removeItem(this.config.storageLeadFormKey);
                    return;
                }

                window.localStorage.setItem(this.config.storageLeadFormKey, JSON.stringify(payload));
            } catch (error) {
                console.log('[AI Chat]: lead form persist failed:', error);
            }
        },

        /**
         * Clears the lead form data stored in the client's local storage using the specified storage key.
         * If an error occurs during the clearing process, it logs an error message to the console.
         *
         * @return {void} This method does not return a value.
         */
        clearLeadFormData() {
            try {
                window.localStorage.removeItem(this.config.storageLeadFormKey);
            } catch (error) {
                console.log('[AI Chat]: lead form clear failed:', error);
            }
        },

        /**
         * Extracts lead form data from the provided DOM element.
         *
         * @param {Object} $form - A jQuery object representing the form from which the data will be extracted.
         * @return {Object} An object containing the lead form data with the following properties:
         *                  - name: {string} The value of the "name" input field.
         *                  - email: {string} The value of the "email" input field.
         *                  - phone: {string} The value of the "phone" input field.
         *                  - company: {string} The value of the "company" input field.
         *                  Returns empty strings if the form is not provided or the fields are not found.
         */
        getLeadFormDataFromDom($form) {
            if (!$form || !$form.length) {
                return {
                    name: '',
                    email: '',
                    phone: '',
                    company: '',
                };
            }

            return {
                name: String($form.find('input[name="name"]').val() || '').trim(),
                email: String($form.find('input[name="email"]').val() || '').trim(),
                phone: String($form.find('input[name="phone"]').val() || '').trim(),
                company: String($form.find('input[name="company"]').val() || '').trim(),
            };
        },

        /**
         * Populates the lead form fields with data retrieved from storage, if available.
         * If no lead form is found or no stored data exists, the method exits without making changes.
         *
         * @return {void} This method does not return a value.
         */
        fillLeadFormFromStorage() {
            const $form = $(this.selectors.messages).find(this.selectors.leadForm);
            if (!$form.length) {
                return;
            }

            const stored = this.getStoredLeadFormData();
            if (!stored) {
                return;
            }

            $form.find('input[name="name"]').val(stored.name);
            $form.find('input[name="email"]').val(stored.email);
            $form.find('input[name="phone"]').val(stored.phone);
            $form.find('input[name="company"]').val(stored.company);
        },
    };

    $(/**
     * Initializes the AIChat module by invoking its init method.
     * This function serves as an entry point for setting up the AIChat system.
     */
    function () {
        AIChat.init();
    });

    window.AIChatWidget = AIChat;

})(jQuery, window, document);
